import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { Auth } from '@shared/auth';
import { Observable } from 'rxjs/Observable';
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { pipe } from 'rxjs/util/pipe';

import { DATASTORE_CONFIG } from './datastore.config';
import { DatastoreConfig } from './datastore.interface';

import { SearchCollections } from '../collections.model';
import {
  backend,
  Backend,
  BackendConfig,
  BackendFetchTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  backendSearch,
  Params,
} from './backend';
import { isDefined } from './helpers';
import { RootModel } from './root.model';
import { RawQuery, Reference, SearchQuery } from './store.model';

export type ApiResponseStatus = 'success' | 'error';

export type ApiFetchResponse<K extends keyof (RootModel & SearchCollections)> =
  | { status: 'success'; result: BackendFetchTypes[K] }
  | { status: 'error'; message: string };

export type ApiPostResponse<K extends keyof RootModel> =
  | { status: 'success'; result: BackendPushReturnTypes[K] }
  | { status: 'error'; message: string };

@Injectable()
export class ApiHttp {
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandler,
    private auth: Auth,
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
  ) {}

  fetch<K extends keyof RootModel>(
    ref: Reference<K>,
  ): Observable<ApiFetchResponse<K>> {
    return Observable.of(ref).pipe(
      switchMap(({ path, query }) =>
        Observable.of((<Backend<K>>backend[path.collection]).fetch).pipe(
          filter(isDefined),
          map(fetch => fetch(path.authUid, path.ids, query)),
          this.fetchHelper(query),
        ),
      ),
    );
  }

  fetchSearch<K extends keyof SearchCollections>(
    key: K,
    query: Observable<SearchQuery>,
  ): Observable<BackendFetchTypes[K]> {
    return query.pipe(
      map(q => (<any>backendSearch[key]).fetch(q)),
      this.fetchHelper<K>(),
      map(response => (response.status === 'success' ? response.result : [])), // FIXME: Error handling?
    );
  }

  private fetchHelper<K extends keyof RootModel>(
    query?: RawQuery<RootModel[K]>,
  ): ((source: Observable<BackendConfig>) => Observable<ApiFetchResponse<K>>);

  private fetchHelper<K extends keyof (RootModel & SearchCollections)>(): ((
    source: Observable<BackendConfig>,
  ) => Observable<ApiFetchResponse<K>>);

  private fetchHelper<K extends keyof (RootModel & SearchCollections)>(
    query?: RawQuery<any>,
  ): ((source: Observable<BackendConfig>) => Observable<ApiFetchResponse<K>>) {
    return pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([config, authHeader]: [BackendConfig, HttpHeaders]) => {
        const baseUrl = config.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;

        const pagination = {
          limit: query && query.limit,
          offset: query && query.offset,
        };

        // Add webapp=1 for tracking in the backend to API requests
        const configParams = config.isGaf
          ? config.params
          : { ...config.params, webapp: 1 };
        const params = pagination
          ? {
              limit: pagination.limit,
              offset: pagination.offset,
              ...configParams,
            }
          : configParams;

        return this.http.get<ApiFetchResponse<K>>(
          `${baseUrl}/${config.endpoint}`,
          {
            headers: authHeader,
            params: this.serialize(params),
          },
        );
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred.
          this.errorHandler.handleError(err.error);
          // FIXME: not sure why TS can't figure out the typings here
          return Observable.of(<{ status: 'error'; message: string }>{
            status: 'error',
            message: err.error.message,
          });
        }
        // The backend returned an unsuccessful response code.
        this.errorHandler.handleError(new Error(err.message));
        // FIXME: not sure why TS can't figure out the typings here
        return Observable.of(<{ status: 'error'; message: string }>{
          status: 'error',
          message: err.message,
        });
      }),
    );
  }

  post<K extends keyof RootModel>(
    config: BackendConfig,
    payload: BackendPushPayloadTypes[K],
    asJSON = true,
  ): Observable<ApiPostResponse<K>> {
    return this.mutate(config, { action: 'post', payload, asJSON });
  }

  put<K extends keyof RootModel>(
    config: BackendConfig,
    payload: BackendPushPayloadTypes[K],
  ): Observable<ApiPostResponse<K>> {
    return this.mutate(config, { action: 'put', payload });
  }

  delete<K extends keyof RootModel>(
    config: BackendConfig,
  ): Observable<ApiPostResponse<K>> {
    return this.mutate(config, { action: 'delete' });
  }

  private mutate<K extends keyof RootModel>(
    config: BackendConfig,
    data:
      | { action: 'post'; payload: BackendPushPayloadTypes[K]; asJSON: boolean }
      | { action: 'put'; payload: BackendPushPayloadTypes[K] }
      | { action: 'delete' },
  ): Observable<ApiPostResponse<K>> {
    return Observable.of(config).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([newConfig, authHeader]) => {
        const baseUrl = newConfig.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;

        const url = `${baseUrl}/${newConfig.endpoint}`;
        const options = { headers: authHeader };

        if (data.action === 'delete') {
          return this.http.delete<ApiPostResponse<K>>(url, options);
        }

        const body = {
          ...(<Params>data.payload), // TODO: https://github.com/Microsoft/TypeScript/issues/15300
          ...newConfig.params,
        };

        if (data.action === 'post') {
          return this.http.post<ApiPostResponse<K>>(
            url,
            data.asJSON ? body : this.serialize(body),
            options,
          );
        }

        return this.http.put<ApiPostResponse<K>>(
          url,
          this.serialize(body),
          options,
        );
      }),
    );
  }

  private serialize(obj: Partial<Params>): HttpParams {
    let params = new HttpParams();
    Object.entries(obj)
      .filter(isDefined)
      .forEach(([key, value]) => {
        if (typeof value === 'string') {
          params = params.set(key, value);
        } else if (typeof value === 'number') {
          params = params.set(key, String(value));
        } else if (Array.isArray(value)) {
          // This type casting shouldn't be necessary but is because of a TS bug.
          // https://github.com/Microsoft/TypeScript/issues/15315
          // https://github.com/Microsoft/TypeScript/issues/7294

          // Our backend expects arrays foo=[1,2] to be encoded as foo[]=1&foo[]=2
          (value as (string | number | undefined)[])
            .filter(isDefined)
            .forEach(rawVal =>
              // The batching of requests connatenates things with `&`.
              String(rawVal)
                .split('&')
                .forEach(v => (params = params.append(`${key}[]`, v))),
            );
        }
      });
    params = params.set('compact', 'true');
    return params;
  }

  /*
  private handleException(error: Response | any) {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
  */
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/api-http.service.ts